import React, { useEffect, useMemo } from 'react'
import { VStack, Text, Box } from '@chakra-ui/react'
import { Controller, useFormContext } from 'react-hook-form'
import type { PickupDTO } from 'ecosystem'
import { GrandRadioCard } from 'ui'
import { overrideText } from 'ui/lib/overrides'
import { CheckoutSectionHeading } from '../CheckoutSectionHeading'
import { PostNordPickupCard } from './PostNordPickupCard'
import type { PostNordCheckoutFormValues } from './types'

interface PostNordPickupSelectorProps {
  pickupOptions: PickupDTO[]
  selectedPickup?: PickupDTO
  overrides?: {
    pickupOptionsTitle?: string
  }
}

export const PostNordPickupSelector: React.FC<PostNordPickupSelectorProps> = ({
  pickupOptions,
  selectedPickup,
  overrides
}) => {
  const name = 'selectedPickup'
  const { control, setValue } = useFormContext<PostNordCheckoutFormValues>()

  // Sort pickup options to show default first
  const sortedPickupOptions = useMemo(() => {
    if (!pickupOptions?.length) return []

    return [...pickupOptions].sort((a, b) => {
      if (a.defaultPickup && !b.defaultPickup) return -1
      if (!a.defaultPickup && b.defaultPickup) return 1
      return 0
    })
  }, [pickupOptions])

  // Auto-select default pickup if none selected
  useEffect(() => {
    if (!selectedPickup && sortedPickupOptions.length > 0) {
      const defaultOption = sortedPickupOptions.find((option) => option.defaultPickup)
      const firstOption = sortedPickupOptions[0]

      if (defaultOption) {
        setValue(name, defaultOption)
      } else if (firstOption) {
        setValue(name, firstOption)
      }
    }
  }, [selectedPickup, sortedPickupOptions, setValue, name])

  if (!sortedPickupOptions?.length) {
    return (
      <VStack align="stretch" spacing={4}>
        <CheckoutSectionHeading>
          {overrideText('Välj upphämtningsställe', overrides?.pickupOptionsTitle)}
        </CheckoutSectionHeading>
        <Box
          p={6}
          borderWidth="1px"
          borderColor="gray.200"
          borderRadius="md"
          textAlign="center"
          color="text.mild"
          bg="gray.50"
        >
          <Text fontSize="md">
            {overrideText('Inga upphämtningsställen tillgängliga', 'No pickup locations available')}
          </Text>
          <Text fontSize="sm" mt={2}>
            {overrideText(
              'Kontakta butiken för upphämtningsarrangemang',
              'Contact the store for pickup arrangements'
            )}
          </Text>
        </Box>
      </VStack>
    )
  }

  return (
    <Controller
      control={control}
      name={name}
      render={({ fieldState: { error } }) => (
        <GrandRadioCard.Root
          value={selectedPickup?.id}
          onChange={(value) =>
            setValue(
              name,
              pickupOptions.find((item) => item.id === value)
            )
          }
          size="md"
          variant="outline"
        >
          <GrandRadioCard.Label fontSize="xl">
            {overrideText('Välj upphämtningsställe', overrides?.pickupOptionsTitle)}
          </GrandRadioCard.Label>

          <VStack alignItems="stretch" gap={4}>
            {sortedPickupOptions.map((pickup) => (
              <PostNordPickupCard pickup={pickup} key={pickup.id} />
            ))}
          </VStack>

          {error && (
            <Text color="red.500" fontSize="sm">
              {error.message}
            </Text>
          )}
        </GrandRadioCard.Root>
      )}
    />
  )
}
